import { makeAutoObservable } from 'mobx';
import {successAlert} from 'shared/tools';
import {DomainStore} from 'shared/store';
import {t} from 'shared/core';
import {endpoints, types} from 'main/tools';
import Location from 'main/stores/locations/Location';
import Department from 'main/stores/departments/Department';
import {VisibilitySetting} from 'main/stores/visibility_settings';
import EMPLOYMENT_TYPES from 'main/stores/employees/employmentTypes';
import _ from 'lodash';

class VisibilitySettingsState {
  store = new DomainStore();

  locations;
  departments;
  visibilitySettings;
  editingVisibilitySetting = {};
  editingVisibilitySettingModalOpen = false;
  errors = {};

  constructor() {
    makeAutoObservable(this, {
      store: false
    });
  }

  async load() {
    await this.store._compose([
      endpoints.LOCATIONS.ALL,
      endpoints.DEPARTMENTS,
      endpoints.VISIBILITY_SETTINGS
    ]);

    this.locations = this.store.getAllOrdered(types.LOCATION, Location);
    this.departments = this.store.getAllOrdered(types.DEPARTMENT, Department);
    this.visibilitySettings = this.store._getAll(types.VISIBILITY_SETTING, VisibilitySetting);
  }

  async updateVisibilitySetting(visibilitySetting, value) {
    visibilitySetting.merge({enabled: value});
    const {model, errors} = await this.store.patch(visibilitySetting);
    this.errors = errors;

    if (model) {
      successAlert(t('company_settings.visibility_settings.Visibility setting updated.'));
    }
  }

  openEditVisibilitySettingModal(visibilitySetting) {
    this.errors = {};
    this.editingVisibilitySetting = new VisibilitySetting(visibilitySetting);
    this.editingVisibilitySettingModalOpen = true;
  }

  closeVisibilitySettingModal() {
    this.editingVisibilitySettingModalOpen = false;
  }

  filterEmptyRules() {
    this.editingVisibilitySetting.segment.rules = _.reject(this.editingVisibilitySetting.segment.rules, r => _.isEmpty(r.parameters));
  }

  async saveVisibilitySetting() {
    this.filterEmptyRules();

    const {model, errors} = await this.store.patch(this.editingVisibilitySetting);

    this.errors = errors;

    if (model) {
      await this.load();
      this.closeVisibilitySettingModal();
    }
  }

  get birthdayCelebration() {
    return _.find(this.visibilitySettings, {settingType: 'birthday_celebration'});
  }

  get startDateCelebration() {
    return _.find(this.visibilitySettings, {settingType: 'start_date_celebration'});
  }

  get anniversaryCelebration() {
    return _.find(this.visibilitySettings, {settingType: 'anniversary_celebration'});
  }

  get staffCount() {
    return _.find(this.visibilitySettings, {settingType: 'staff_count'});
  }

  get peopleOffToday() {
    return _.find(this.visibilitySettings, {settingType: 'people_off_today'});
  }

  get peopleDirectory() {
    return _.find(this.visibilitySettings, {settingType: 'people_directory'});
  }

  get employmentTypes() {
    return EMPLOYMENT_TYPES.map(type => {
      return {id: type, name: t(type)};
    });
  }

  formattedRulesForSegment(segment) {
    let selectedRules = [];

    if (segment.locationRule) {
      const selectedLocations = _.filter(this.locations, l => _.includes(segment.locationRule.parameters, l.id));
      selectedRules = _.concat(selectedRules, selectedLocations);
    }

    if (segment.departmentRule) {
      const selectedDepartments = _.filter(this.departments, l => _.includes(segment.departmentRule.parameters, l.id));
      selectedRules = _.concat(selectedRules, selectedDepartments);
    }

    if (segment.employmentTypeRule) {
      const selectedEmploymentTypes = _.filter(this.employmentTypes, l => _.includes(segment.employmentTypeRule.parameters, l.id));
      selectedRules = _.concat(selectedRules, selectedEmploymentTypes);
    }

    return _.map(selectedRules, 'name');
  }
}

export default VisibilitySettingsState;
